import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Lock, Users, Copy, Check, ArrowRight, Crown } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

const TB_LABELS = {
  QB: "passing", RB: "rushing", WR: "receiving", TE: "receiving", DEF: "defensive",
};

export default function CreateChallenge() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [form, setForm] = useState({
    sport: "NFL",
    position: "QB",
    title: "",
    lock_time: "",
    settle_hours: 6,
    max_entries: 20,
    tiebreaker_key: "",
  });
  const [tiebreakers, setTiebreakers] = useState([]);
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  usePageMeta({ title: "New Challenge · PickemSports.club", path: "/friends/new", noindex: true });

  useEffect(() => {
    api.get("/friends/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
    // default kickoff: next hour, rounded
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setMinutes(0, 0, 0);
    setForm((f) => ({ ...f, lock_time: d.toISOString().slice(0, 16) }));
  }, []);

  useEffect(() => {
    api.get("/scoring/rules")
      .then(({ data }) => {
        const list = data.tiebreakers?.[form.position] || [];
        setTiebreakers(list);
        setForm((f) => ({ ...f, tiebreaker_key: list[0]?.key || "" }));
      })
      .catch(() => setTiebreakers([]));
  }, [form.position]);

  const submit = async () => {
    if (form.title.trim().length < 3) return toast.error("Give your challenge a name");
    if (!form.lock_time) return toast.error("Pick a lock time");
    setBusy(true);
    try {
      const { data } = await api.post("/friends/challenges", {
        ...form,
        title: form.title.trim(),
        lock_time: new Date(form.lock_time).toISOString(),
        max_entries: Number(form.max_entries),
        settle_hours: Number(form.settle_hours),
        invite_emails: emails.split(/[\s,;]+/).filter((e) => e.includes("@")),
      });
      setCreated(data);
      toast.success("Challenge created — share the code");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the challenge");
    } finally { setBusy(false); }
  };

  const shareUrl = created ? `${window.location.origin}/api/invite/${created.invite_code}` : "";

  const copy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Invite link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const positions = catalog?.sports?.find((s) => s.id === form.sport)?.positions || ["QB"];

  if (created) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto py-10 text-center" data-testid="challenge-created">
          <div className="w-14 h-14 rounded-full bg-[#00FF66] flex items-center justify-center mx-auto mb-5">
            <Crown size={26} className="text-black" />
          </div>
          <div className="text-[#00FF66] text-xs uppercase tracking-widest mb-2">You're the manager</div>
          <h1 className="font-display text-4xl mb-3">CHALLENGE IS LIVE</h1>
          <p className="text-white/50 text-sm mb-6">
            Send this code to your crew. Up to {created.max_entries} friends can join — free, no cash prizes.
          </p>
          <div className="border border-white/10 bg-[#141414] p-5 mb-4">
            <div className="text-white/40 text-[10px] uppercase tracking-widest mb-1">Invite code</div>
            <div className="font-display text-5xl tracking-[0.25em] text-[#00FF66]" data-testid="created-invite-code">
              {created.invite_code}
            </div>
            <button
              onClick={copy}
              data-testid="copy-invite-link-btn"
              className="mt-4 w-full py-2.5 border border-white/20 hover:border-white/40 text-white text-xs uppercase tracking-widest font-bold rounded-sm inline-flex items-center justify-center gap-2"
            >
              {copied ? <Check size={13} className="text-[#00FF66]" /> : <Copy size={13} />}
              {copied ? "Copied" : "Copy invite link"}
            </button>
            {created.invites_sent > 0 && (
              <div className="mt-2 text-[#00FF66] text-[10px] uppercase tracking-widest">
                {created.invites_sent} email invite{created.invites_sent > 1 ? "s" : ""} sent
              </div>
            )}
          </div>
          <button
            onClick={() => navigate(`/contest/${created.contest_id}`)}
            data-testid="open-challenge-btn"
            className="bg-[#00FF66] hover:bg-[#00e65b] text-black font-bold uppercase tracking-widest px-5 py-3 text-sm rounded-sm inline-flex items-center gap-2"
          >
            Make my picks <ArrowRight size={15} />
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <div className="text-[#00FF66] text-xs uppercase tracking-widest mb-2">Friends Only</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">NEW CHALLENGE</h1>
          <p className="text-white/50 text-sm mt-2">
            Free for everyone you invite. No entry fee, no pot, no cash prize — bragging rights only.
          </p>
        </div>

        <div className="border border-white/10 bg-[#141414] p-5 space-y-4">
          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-widest">Challenge name</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Sunday Squad Showdown"
              maxLength={60}
              data-testid="challenge-title-input"
              className="w-full mt-1 bg-[#0a0a0a] border border-white/10 focus:border-[#00FF66] rounded-sm px-3 py-2.5 outline-none"
            />
          </div>

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-widest">Sport</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {(catalog?.sports || []).map((s) => (
                <button
                  key={s.id}
                  onClick={() => s.live && setForm({ ...form, sport: s.id })}
                  disabled={!s.live}
                  data-testid={`challenge-sport-${s.id}`}
                  className={`px-2.5 py-1.5 text-xs uppercase tracking-widest rounded-sm border inline-flex items-center gap-1.5 ${
                    !s.live
                      ? "border-white/5 text-white/25 cursor-not-allowed"
                      : form.sport === s.id
                      ? "border-[#00FF66] text-[#00FF66] bg-[#00FF66]/10"
                      : "border-white/15 text-white/60 hover:border-white/40"
                  }`}
                >
                  {!s.live && <Lock size={10} />} {s.label}
                  {!s.live && <span className="text-[9px] normal-case tracking-normal">soon</span>}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-widest">Position — pick the top 5</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {positions.map((p) => (
                <button
                  key={p}
                  onClick={() => setForm({ ...form, position: p })}
                  data-testid={`challenge-position-${p}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-widest rounded-sm border ${
                    form.position === p
                      ? "border-[#00FF66] text-[#00FF66] bg-[#00FF66]/10"
                      : "border-white/15 text-white/60 hover:border-white/40"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-widest">
              Tiebreaker — you choose the {TB_LABELS[form.position] || ""} question
            </label>
            <select
              value={form.tiebreaker_key}
              onChange={(e) => setForm({ ...form, tiebreaker_key: e.target.value })}
              data-testid="challenge-tiebreaker-select"
              className="w-full mt-1 bg-[#0a0a0a] border border-white/10 focus:border-[#00FF66] rounded-sm px-3 py-2.5 outline-none text-sm"
            >
              {tiebreakers.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-widest">Picks lock at</label>
              <input
                type="datetime-local"
                value={form.lock_time}
                onChange={(e) => setForm({ ...form, lock_time: e.target.value })}
                data-testid="challenge-lock-input"
                className="w-full mt-1 bg-[#0a0a0a] border border-white/10 focus:border-[#00FF66] rounded-sm px-3 py-2.5 outline-none text-sm"
              />
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-widest">Scores for (hours)</label>
              <input
                type="number"
                min={1}
                max={72}
                value={form.settle_hours}
                onChange={(e) => setForm({ ...form, settle_hours: e.target.value })}
                data-testid="challenge-settle-input"
                className="w-full mt-1 bg-[#0a0a0a] border border-white/10 focus:border-[#00FF66] rounded-sm px-3 py-2.5 outline-none text-sm"
              />
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-widest">
                Max players (≤ {catalog?.max_field || 100})
              </label>
              <input
                type="number"
                min={2}
                max={catalog?.max_field || 100}
                value={form.max_entries}
                onChange={(e) => setForm({ ...form, max_entries: e.target.value })}
                data-testid="challenge-cap-input"
                className="w-full mt-1 bg-[#0a0a0a] border border-white/10 focus:border-[#00FF66] rounded-sm px-3 py-2.5 outline-none text-sm"
              />
            </div>
          </div>

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-widest">
              Invite by email (optional — comma separated)
            </label>
            <textarea
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              rows={2}
              placeholder="mike@example.com, sara@example.com"
              data-testid="challenge-emails-input"
              className="w-full mt-1 bg-[#0a0a0a] border border-white/10 focus:border-[#00FF66] rounded-sm px-3 py-2.5 outline-none text-sm"
            />
          </div>

          <button
            onClick={submit}
            disabled={busy}
            data-testid="challenge-submit-btn"
            className="w-full py-3 bg-[#00FF66] hover:bg-[#00e65b] disabled:opacity-50 text-black font-bold uppercase tracking-widest text-sm rounded-sm inline-flex items-center justify-center gap-2"
          >
            <Users size={15} /> {busy ? "Creating..." : "Create challenge"}
          </button>
          <div className="text-white/30 text-[10px] text-center">
            Every player needs their own membership to join · results score off live NFL stats
          </div>
        </div>
      </div>
    </Layout>
  );
}
